import {css, StyleSheet} from 'aphrodite';
import React, {useEffect} from 'react';

import {setWindowTitle} from 'BenchlingUtil';
import navigateToURL from 'lib/navigateToURL';
import {FONT_PROXIMA_13, FONT_PROXIMA_17} from 'styles/FontConstants';
import {GRAY_DARKER, BLUE_LIGHT, GRAY_LIGHT} from 'ui-library/Colors';
import {Header1} from 'ui-library/Headers';
import Link from 'ui-library/Link';

export enum MONOMER_CONFIGURATION_PURPOSE {
  CREATE = 'CREATE',
  EDIT = 'EDIT',
  VIEW = 'VIEW',
  CUSTOM_NOTATION = 'CUSTOM_NOTATION',
  IDT_ALIASES = 'IDT_ALIASES',
}

const PURPOSE_TO_TITLE: {[key in MONOMER_CONFIGURATION_PURPOSE]: string} = {
  [MONOMER_CONFIGURATION_PURPOSE.CREATE]: 'Add monomers',
  [MONOMER_CONFIGURATION_PURPOSE.EDIT]: 'Edit monomers',
  [MONOMER_CONFIGURATION_PURPOSE.VIEW]: 'View monomers',
  [MONOMER_CONFIGURATION_PURPOSE.CUSTOM_NOTATION]: 'Custom notations',
  [MONOMER_CONFIGURATION_PURPOSE.IDT_ALIASES]: 'IDT aliases',
};

export interface MonomerConfigurationPageContainerProps {
  purpose?: MONOMER_CONFIGURATION_PURPOSE;
  children?: React.ReactNode;
  methodTitle?: string;
}

export default function MonomerConfigurationPageContainer({
  children,
  methodTitle,
  purpose = MONOMER_CONFIGURATION_PURPOSE.CREATE,
}: MonomerConfigurationPageContainerProps): JSX.Element {
  const title = PURPOSE_TO_TITLE[purpose];
  useEffect(() => {
    setWindowTitle(title);
  }, [title]);

  return (
    <div
      className={css(styles.container, purpose !== MONOMER_CONFIGURATION_PURPOSE.EDIT && styles.marginBottom)}
    >
      <div className={css(styles.header)}>
        <span
          className={css(styles.library)}
          onClick={() => {
            navigateToURL('/monomers');
          }}
        >
          Monomer library /
        </span>
        <div className={css(styles.subtitle)}>{title}</div>
      </div>
      <div
        className={css(
          styles.borderBottom,
          purpose !== MONOMER_CONFIGURATION_PURPOSE.EDIT && styles.marginBottom
        )}
      />
      <div className={css(styles.creationMethodSection)}>
        {methodTitle && (
          <div className={css(styles.creationMethodHeader)}>
            <Header1>{methodTitle}</Header1>
            {purpose === MONOMER_CONFIGURATION_PURPOSE.CREATE && (
              <div className={css(styles.changeMethod)}>
                <Link
                  onMouseDown={() => {
                    navigateToURL('/monomers/create');
                  }}
                >
                  Change method
                </Link>
              </div>
            )}
          </div>
        )}
        <div>{children}</div>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  creationMethodSection: {
    marginLeft: 40,
  },
  changeMethod: {
    paddingBottom: 2,
  },
  creationMethodHeader: {
    display: 'flex',
    flexDirection: 'row',
    gap: 12,
    alignItems: 'center',
  },
  header: {
    marginLeft: 40,
  },
  borderBottom: {
    borderBottom: `1px solid ${GRAY_LIGHT}`,
    marginTop: 16,
  },
  subtitle: FONT_PROXIMA_17,
  library: {
    color: GRAY_DARKER,
    cursor: 'pointer',
    ...FONT_PROXIMA_13,
    ':hover': {
      color: BLUE_LIGHT,
    },
  },
  container: {
    marginTop: 40,
  },
  marginBottom: {
    marginBottom: 16,
  },
});
